import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ExternalLink, LogIn, Play, Search, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { videosApi } from '../services/api';
import './HRHub.css';

function getEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (u.hostname.includes('youtu.be')) {
      return u.pathname.slice(1) ? `https://www.youtube.com/embed/${u.pathname.slice(1)}` : null;
    }
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean)[0];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch (_) {
    return null;
  }
  return null;
}

function PublicTrainingVideos() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [activeVideo, setActiveVideo] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadVideos() {
      try {
        const data = await videosApi.getPublished();
        if (mounted) setVideos(Array.isArray(data) ? data : []);
      } catch (err) {
        if (mounted) setError(err.message || 'Failed to load videos');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadVideos();
    return () => { mounted = false; };
  }, []);

  const visibleVideos = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = videos.filter((video) => {
      if (!q) return true;
      const title = video.title?.toLowerCase() || '';
      const description = video.description?.toLowerCase() || '';
      const tags = (video.tags || []).map((tag) => tag.name?.toLowerCase()).join(' ');
      return title.includes(q) || description.includes(q) || tags.includes(q);
    });

    return [...filtered].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [query, videos]);

  function openVideo(video) {
    setPlaying(false);
    setActiveVideo(video);
  }

  return (
    <div className="hh-root hh-public-video-root">
      <main className="hh-main">
        <header className="hh-header">
          <div className="hh-header-title">
            <div className="hh-breadcrumb">
              <span>Employee portal</span>
              <ChevronRight size={12} strokeWidth={2} />
              <span style={{ color: 'var(--hh-text-body)', fontWeight: 600 }}>HR Hub</span>
            </div>
            <div className="hh-title-row">
              <h1 className="hh-title">Video tutorials</h1>
              <span className="hh-count-pill">{visibleVideos.length}</span>
            </div>
          </div>

          <div className="hh-header-search">
            <label className="hh-search">
              <Search size={18} />
              <input
                className="hh-search-input"
                type="text"
                placeholder="Search documents, guides and videos..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search training videos"
              />
            </label>
          </div>

          <div className="hh-header-actions">
            <button type="button" className="hh-btn-primary" onClick={() => navigate('/login')}>
              <LogIn size={17} strokeWidth={1.9} />
              Login
            </button>
          </div>
        </header>

        <div className="hh-scroll">
          <div className="hh-content">
            <section className="hh-section">
              <div className="hh-section-head">
                <h2 className="hh-section-title">Video tutorials</h2>
                <span className="hh-section-sub">Step-by-step walkthroughs</span>
              </div>

              {loading ? (
                <div className="hh-video-grid">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="hh-skel" style={{ height: 252, borderRadius: 'var(--hh-radius-lg)' }} />
                  ))}
                </div>
              ) : error ? (
                <div className="hh-empty">
                  <span className="hh-empty-icon"><X size={32} strokeWidth={1.75} /></span>
                  <h3 style={{ margin: 0, fontFamily: 'var(--hh-font-display)', fontWeight: 700, fontSize: 'var(--hh-text-xl)', color: 'var(--hh-text-strong)' }}>
                    Failed to load videos
                  </h3>
                  <p style={{ margin: '8px 0 18px', fontSize: 'var(--hh-text-md)', color: 'var(--hh-text-muted)', maxWidth: 380 }}>{error}</p>
                  <button type="button" className="hh-btn-primary" style={{ boxShadow: 'none' }} onClick={() => window.location.reload()}>
                    Try again
                  </button>
                </div>
              ) : visibleVideos.length === 0 ? (
                <div className="hh-empty">
                  <span className="hh-empty-icon"><Search size={32} strokeWidth={1.75} /></span>
                  <h3 style={{ margin: 0, fontFamily: 'var(--hh-font-display)', fontWeight: 700, fontSize: 'var(--hh-text-xl)', color: 'var(--hh-text-strong)' }}>
                    No resources found
                  </h3>
                </div>
              ) : (
                <div className="hh-video-grid">
                  {visibleVideos.map((video) => {
                    const tag = video.tags?.[0]?.name || 'Video';
                    return (
                      <button key={video.id} type="button" className="hh-video-card" onClick={() => openVideo(video)} aria-label={`Play ${video.title}`}>
                        <div className="hh-video-thumb">
                          <span className="hh-video-cat">{tag}</span>
                          <span className="hh-video-play"><Play size={22} fill="currentColor" /></span>
                          {video.duration && <span className="hh-video-duration">{video.duration}</span>}
                        </div>
                        <div className="hh-video-body">
                          <div className="hh-video-title">{video.title}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {activeVideo && (
        <PublicVideoModal
          video={activeVideo}
          playing={playing}
          onPlay={() => setPlaying(true)}
          onClose={() => setActiveVideo(null)}
        />
      )}
    </div>
  );
}

function PublicVideoModal({ video, playing, onPlay, onClose }) {
  const embedUrl = getEmbedUrl(video.video_url);
  const proxyUrl = videosApi.viewUrl(video.id);
  const openUrl = embedUrl ? video.video_url : proxyUrl;
  const tag = video.tags?.[0]?.name || 'Video';

  return (
    <div className="hh-overlay" onClick={onClose}>
      <div className="hh-modal hh-modal-video" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--hh-border-subtle)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--hh-font-display)', fontWeight: 700, fontSize: 'var(--hh-text-lg)', color: 'var(--hh-text-strong)' }}>{video.title}</div>
            <div style={{ fontSize: 'var(--hh-text-sm)', color: 'var(--hh-text-muted)', marginTop: 1 }}>{tag}</div>
          </div>
          <button type="button" className="hh-modal-close" aria-label="Close" onClick={onClose}>
            <X size={19} strokeWidth={2} />
          </button>
        </div>

        <div className="hh-modal-player">
          {playing ? (
            embedUrl ? (
              <iframe src={`${embedUrl}?autoplay=1`} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            ) : (
              <video src={proxyUrl} controls autoPlay />
            )
          ) : (
            <>
              <button type="button" className="hh-play-lg" aria-label="Play video" onClick={onPlay}>
                <Play size={30} fill="currentColor" />
              </button>
              <span style={{ fontSize: 'var(--hh-text-sm)', color: 'var(--hh-neutral-400)' }}>Click to play the tutorial</span>
            </>
          )}
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', justifyContent: 'flex-end' }}>
          {openUrl && (
            <a className="hh-btn-outline" style={{ height: 40 }} href={openUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={16} strokeWidth={1.75} />
              Open in new tab
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default PublicTrainingVideos;
